import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useEntity } from "@/context/EntityContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Lock, CheckCircle2, Building2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { value: "personal", label: "Personal" },
  { value: "business", label: "Business" },
  { value: "freelance", label: "Freelance / Side hustle" },
  { value: "rental", label: "Rental / Landlord" },
  { value: "other", label: "Other" },
];

const COLORS = ["#2F4F4F", "#4C956C", "#E07A5F", "#E63946", "#6B4E71", "#D4A017", "#3D5A80", "#B4614E"];
const ICONS = ["👤", "🏢", "💼", "🏠", "🎨", "🚗", "🌱", "📈", "🎯", "🎓", "💡", "🍽", "✈", "🐾", "🎬"];

export default function Entities() {
  const { entities, currentEntityId, setEntity, refresh } = useEntity();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "business", icon: "🏢", color: "#4C956C" });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", kind: "business", icon: "🏢", color: "#4C956C" });
    setOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({ name: e.name, kind: e.kind, icon: e.icon, color: e.color });
    setOpen(true);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    try {
      if (editing) {
        await api.patch(`/entities/${editing.id}`, {
          name: form.name.trim(), kind: form.kind, icon: form.icon, color: form.color,
        });
        toast.success("Entity updated");
      } else {
        const { data } = await api.post("/entities", {
          name: form.name.trim(), kind: form.kind, icon: form.icon, color: form.color,
        });
        toast.success(`Created "${data.name}"`);
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save entity");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/entities/${deleteTarget.id}`);
      if (currentEntityId === deleteTarget.id) {
        const def = entities.find((e) => e.is_default);
        if (def) setEntity(def.id);
      }
      toast.success("Entity deleted");
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
      setDeleteTarget(null);
    }
  };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">Books</div>
          <h1 className="font-display text-4xl mt-2">Entities</h1>
          <p className="text-sm text-[var(--muted-text)] mt-1 max-w-xl">
            Keep separate books for personal, business, side-hustle, or rental — each with its own accounts, transactions, and financial statements. Switch anytime from the top bar, or view everything consolidated.
          </p>
        </div>
        <Button
          data-testid="add-entity-btn"
          onClick={openCreate}
          className="rounded-full bg-[#E07A5F] hover:bg-[#c9694f] text-white h-11 px-5"
        >
          <Plus className="w-4 h-4 mr-2" /> New entity
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="entities-list">
        {entities.map((e) => {
          const isActive = currentEntityId === e.id;
          return (
            <div
              key={e.id}
              data-testid={`entity-card-${e.name}`}
              className={`card-flat p-5 flex flex-col gap-3 relative overflow-hidden ${isActive ? "ring-2 ring-[#2F4F4F]" : ""}`}
            >
              {isActive && (
                <span data-testid={`entity-active-${e.name}`} className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] text-[#2F6B4A] bg-[#EAF4EE] px-2 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" /> Active
                </span>
              )}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-md flex items-center justify-center text-2xl shrink-0"
                     style={{ backgroundColor: `${e.color}22`, color: e.color }}>
                  {e.icon}
                </div>
                <div className="min-w-0">
                  <div className="text-lg font-medium truncate">{e.name}</div>
                  <div className="text-xs text-[var(--muted-text)] capitalize flex items-center gap-1">
                    {e.is_default ? <><Lock className="w-3 h-3" /> Default · </> : null}
                    {e.kind.replace("_", " ")}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!isActive && (
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`entity-switch-${e.name}`}
                    onClick={() => { setEntity(e.id); toast.success(`Switched to ${e.name}`); }}
                    className="flex-1"
                  >
                    Switch to
                  </Button>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  data-testid={`entity-edit-${e.name}`}
                  onClick={() => openEdit(e)}
                  aria-label="Edit entity"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  data-testid={`entity-delete-${e.name}`}
                  onClick={() => setDeleteTarget(e)}
                  disabled={e.is_default}
                  title={e.is_default ? "Default entity cannot be deleted" : "Delete"}
                  aria-label="Delete entity"
                >
                  <Trash2 className={`w-4 h-4 ${e.is_default ? "opacity-30" : "text-[#E63946]"}`} />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 card-flat p-5 border-l-4 border-l-[#D4A017] max-w-2xl">
        <div className="flex items-start gap-3">
          <Building2 className="w-5 h-5 text-[#D4A017] shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-medium mb-1">What's scoped per entity</div>
            <div className="text-[var(--muted-text)]">
              In this build, <b>transactions</b>, <b>wallets/accounts</b>, and all <b>accounting reports</b> (P&amp;L, Balance Sheet, Trial Balance, GL, Cash Flow, Monthly Close) are separated by entity. Choose <b>Consolidated</b> in the top-bar switcher to see everything combined. Categories, budgets, goals, and investments are shared across entities in this version.
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit entity" : "New entity"}</DialogTitle>
            <DialogDescription>Each entity keeps its own books. You can switch anytime from the top-bar dropdown.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-md flex items-center justify-center text-2xl shrink-0"
                   style={{ backgroundColor: `${form.color}22`, color: form.color }}>
                {form.icon}
              </div>
              <div className="flex-1 space-y-2">
                <Label>Name</Label>
                <Input
                  data-testid="entity-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Side Business, Rental Portfolio"
                  required
                  maxLength={40}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="entity-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="flex flex-wrap gap-1.5">
                {ICONS.map((i) => (
                  <button
                    type="button"
                    key={i}
                    data-testid={`entity-icon-${i}`}
                    onClick={() => setForm({ ...form, icon: i })}
                    className={`w-9 h-9 rounded-md text-lg flex items-center justify-center ${form.icon === i ? "bg-[#2F4F4F] text-[#FDFCF8]" : "bg-[var(--hover)]"}`}
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    data-testid={`entity-color-${c}`}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-[#2F4F4F] scale-110" : ""}`}
                    style={{ backgroundColor: c }}
                    aria-label={`Choose color ${c}`}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                data-testid="entity-submit"
                type="submit"
                disabled={!form.name.trim()}
                className="rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
              >
                {editing ? "Save changes" : "Create entity"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              You must move or delete this entity's transactions and wallets first — otherwise this action will fail with a helpful message.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="entity-delete-confirm" onClick={confirmDelete} className="bg-[#E63946] hover:bg-[#c92e3b] text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
